"use client";

import { ArrowRight, ArrowUp, Check, FileText } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";
import { Brand } from "@/components/brand";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { withBase } from "@/lib/base-path";
import { SiteFooter } from "./site-footer";
import "./homepage-3d.css";

const RAIL = [
  { id: "awal", label: "Mulai" },
  { id: "jalur", label: "Pilih jalur" },
  { id: "dokumen", label: "Dokumen" },
  { id: "form", label: "Isi form" },
  { id: "mesin", label: "Hitung harga" },
  { id: "kirim", label: "Kirim" },
  { id: "review", label: "Review" },
];

const DOCS = [
  "PIB (impor) atau faktur pembelian dari pabrik (dalam negeri)",
  "Konfigurasi produk (Excel) dan faktur pajak pembelian lokal",
  "Laporan laba/rugi dan bukti biaya promosi",
  "Bukti BPJS karyawan dan surat pernyataan investasi",
];

/**
 * Layout beranda 3D: adegan Three.js (latar tetap) yang kameranya bergerak mengikuti scroll, menceritakan alur
 * pilih jalur → dokumen → form → mesin hitung harga → kirim → tunggu review. Dipilih lewat `HOMEPAGE_TEMPLATE`
 * di `app/page.tsx`.
 */
export function Homepage3d() {
  const rootRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const [footerVisible, setFooterVisible] = useState(false);

  useEffect(() => {
    const root = rootRef.current;
    const canvas = canvasRef.current;
    if (!root || !canvas) return;
    let unmount: (() => void) | undefined;
    let cancelled = false;
    // Three.js dimuat terpisah agar tidak memperberat bundel halaman lain.
    import("./homepage-3d-scene").then(({ mountHomepageScene }) => {
      if (!cancelled) unmount = mountHomepageScene({ canvas, root, logoUrl: withBase("/logo-kemenkes.png") });
    });
    const html = document.documentElement;
    const prevBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    // Navigasi langkah disembunyikan saat footer terlihat agar tidak menumpuk.
    const io = new IntersectionObserver((entries) => setFooterVisible(entries.some((e) => e.isIntersecting)));
    if (footerRef.current) io.observe(footerRef.current);
    // Header transparen (latarnya adegan 3D), jadi kartu dipudarkan saat mendekati header agar teksnya tidak
    // menumpuk dengan logo & tombol.
    const cards = [...root.querySelectorAll<HTMLElement>(".home3d-card")];
    let ticking = false;
    const fade = () => {
      ticking = false;
      const limit = headerRef.current?.offsetHeight ?? 0;
      cards.forEach((c) => {
        // Penuh saat ≥ 100 px di bawah header, hilang sebelum menyentuh teks header.
        const o = Math.min(1, Math.max(0, (c.getBoundingClientRect().top - limit + 20) / 120));
        c.style.opacity = o < 1 ? String(o) : "";
      });
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(fade);
      }
    };
    addEventListener("scroll", onScroll, { passive: true });
    addEventListener("resize", onScroll);
    fade();
    return () => {
      cancelled = true;
      unmount?.();
      io.disconnect();
      removeEventListener("scroll", onScroll);
      removeEventListener("resize", onScroll);
      html.style.scrollBehavior = prevBehavior;
    };
  }, []);

  return (
    <div ref={rootRef} className="home3d">
      <canvas ref={canvasRef} className="home3d-canvas" aria-hidden="true" />
      <div className="home3d-veil" aria-hidden="true" />

      <header ref={headerRef} className="fixed inset-x-0 top-0 z-40">
        {/* Satu baris di semua lebar layar: di ponsel nama & label tombol dipersingkat. */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 sm:gap-4 sm:px-8 lg:px-12">
          <div className="min-w-0 md:hidden">
            <Brand />
          </div>
          <div className="hidden md:block">
            <Brand title="Kurasi Harga Alat Kesehatan" />
          </div>
          <div className="flex shrink-0 gap-2 sm:gap-2.5">
            {/* Layar sangat sempit (< 360 px): cukup tombol Daftar agar nama tetap terbaca. */}
            <Button
              variant="outline"
              className="h-9 px-3 text-[13px] max-[359px]:hidden sm:h-10 sm:px-4 sm:text-sm"
              asChild
            >
              <Link href="/masuk">Masuk</Link>
            </Button>
            <Button className="h-9 px-3 text-[13px] sm:h-10 sm:px-4 sm:text-sm" asChild>
              <Link href="/daftar">
                <span className="md:hidden">Daftar</span>
                <span className="hidden md:inline">Daftar sebagai Penyedia</span>
              </Link>
            </Button>
          </div>
        </div>
      </header>

      <nav aria-label="Langkah alur kurasi" className="home3d-rail" data-hidden={footerVisible || undefined}>
        <ol>
          {RAIL.map((r) => (
            <li key={r.id}>
              <a href={`#${r.id}`} data-rail>
                <span>{r.label}</span>
                <i aria-hidden="true" />
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <main className="relative z-[2]">
        <section className="home3d-step home3d-hero" id="awal" data-step>
          <div className="home3d-card">
            <Badge className="px-3 py-1 text-xs">Kurasi harga e-Katalog alat kesehatan</Badge>
            <h1 className="mt-4 mb-3 text-3xl leading-[1.12] font-extrabold tracking-tight sm:text-[42px]">
              Dari dokumen sampai keputusan, dalam satu alur yang sama untuk semua penyedia.
            </h1>
            <p>
              Gulir untuk melihat perjalanan sebuah pengajuan: memilih jalur produk, menyiapkan dokumen, mengisi form,
              dihitung mesin harga, dikirim, lalu ditinjau kurator.
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <Button asChild>
                <Link href="/daftar">
                  Mulai Pengajuan <ArrowRight aria-hidden="true" />
                </Link>
              </Button>
              <Button variant="outline" asChild>
                <a href="#jalur">Lihat alurnya</a>
              </Button>
            </div>
          </div>
        </section>

        <Step id="jalur" no="01" title="Pilih jalur produk">
          <p>
            Mulai dari izin edar produk Anda. Jalur ini menentukan dokumen yang diminta dan komponen biaya yang
            dihitung.
          </p>
          <div className="mt-2 grid gap-2.5 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 rounded-2xl border bg-card p-3.5 text-[13px] leading-relaxed text-subtle-foreground">
              <Badge variant="info" className="self-start">
                AKL
              </Badge>
              <strong className="text-[15px] text-foreground">Produk impor</strong>
              Datang lewat pelabuhan. Harga pokok dari dokumen PIB (CIF, FOB, atau EXW).
            </div>
            <div className="flex flex-col gap-1.5 rounded-2xl border bg-card p-3.5 text-[13px] leading-relaxed text-subtle-foreground">
              <Badge className="self-start">AKD</Badge>
              <strong className="text-[15px] text-foreground">Produk dalam negeri</strong>
              Dari pabrik lokal. Harga pokok dari faktur pembelian pabrik sebelum PPN.
            </div>
          </div>
        </Step>

        <Step id="dokumen" no="02" title="Siapkan dokumen">
          <p>Kumpulkan dokumen pendukung sebelum mulai, supaya pengajuan bisa langsung dikirim.</p>
          <ul className="mt-1.5 flex flex-col gap-2 text-sm leading-normal">
            {DOCS.map((d) => (
              <li key={d} className="flex items-start gap-2.5">
                <FileText className="mt-0.5 size-4 shrink-0 text-primary" aria-hidden="true" />
                {d}
              </li>
            ))}
          </ul>
        </Step>

        <Step id="form" no="03" title="Isi form pengajuan">
          <p>
            Masukkan data produk, harga pokok, dan biaya operasional. Draft tersimpan otomatis, jadi Anda bisa
            melanjutkan kapan saja.
          </p>
        </Step>

        <Step id="mesin" no="04" title="Mesin hitung harga bekerja">
          <p>
            Landed cost, biaya operasional, margin, pajak, dan PNBP dihitung dengan rumus baku yang sama untuk semua
            penyedia. Harga maksimum e-Katalog langsung tampil.
          </p>
          <div className="mt-1.5 flex flex-col gap-0.5 rounded-2xl bg-primary-soft px-4 py-3.5">
            <span className="text-[13px] text-subtle-foreground">Harga maksimum e-Katalog</span>
            <strong className="num text-3xl font-extrabold tracking-tight text-primary-strong" data-price>
              Rp 0
            </strong>
          </div>
        </Step>

        <Step id="kirim" no="05" title="Tentukan penawaran & kirim">
          <p>
            Isi harga penawaran, unggah dokumen, lalu kirim. Pengajuan Anda mendapat nomor dan masuk antrean kurasi.
          </p>
          <p className="flex items-center gap-2 rounded-xl bg-[var(--status-approved-bg)] px-3.5 py-2.5 text-sm font-bold text-[var(--status-approved-fg)]">
            <Check className="size-4 shrink-0" strokeWidth={2.6} aria-hidden="true" />
            Penawaran Rp 305.000.000 berada di bawah batas
          </p>
        </Step>

        <Step id="review" no="06" title="Selesai, tunggu review">
          <p>Kurator Kemenkes meninjau pengajuan. Status dan catatan dikirim ke email dan tampil di beranda Anda.</p>
          <Badge variant="warning" className="px-3.5 py-2 text-sm">
            <span className="home3d-pulse" aria-hidden="true" />
            Menunggu review kurator
          </Badge>
          <div className="mt-5 flex flex-wrap gap-2.5">
            <Button asChild>
              <Link href="/daftar">
                Mulai Pengajuan <ArrowRight aria-hidden="true" />
              </Link>
            </Button>
            <Button variant="outline" asChild>
              <a href="#awal">
                Ulangi dari awal <ArrowUp aria-hidden="true" />
              </a>
            </Button>
          </div>
        </Step>
      </main>

      <div ref={footerRef} className="relative z-[2]">
        <SiteFooter />
      </div>
    </div>
  );
}

function Step({ id, no, title, children }: { id: string; no: string; title: string; children: React.ReactNode }) {
  return (
    <section className="home3d-step" id={id} data-step aria-labelledby={`${id}-judul`}>
      <div className="home3d-card">
        <span className="text-xs font-bold tracking-wider text-primary-strong uppercase">Langkah {no}</span>
        <h2
          id={`${id}-judul`}
          className="mt-2 mb-2.5 text-2xl leading-tight font-extrabold tracking-tight sm:text-[32px]"
        >
          {title}
        </h2>
        {children}
      </div>
    </section>
  );
}
